// Nav item shape:
// {
//   key:           String   — unique React key
//   label:         String   — i18n key passed to window.I18n.t()
//   href:          String   — URL (or '#' for dropdown triggers)
//   icon:          String   — CSS class applied to the li (e.g. 'icon-trainings')
//   dataPurpose:   String   — data-purpose attribute value
//   isSection:     Boolean  — renders as a labelled section group with NavList children
//   isDropdown:    Boolean  — renders as sub-admin-container with js-dropdown-toggle
//   children:      Array    — sub-items (same shape, no nesting beyond one level)
//   activeStateKey:String   — key in props.activeStates for namespace-based active detection
//   external:      Boolean  — open in new tab
// }

// ─── Learner ────────────────────────────────────────────────────────────────

export const getLearnerNavConfig = (routes, permissions) => {
  if (permissions.isDendreoUser) {
    return [
      {
        key: 'my_trainings',
        label: 'js.navigation.my_trainings',
        href: permissions.dendreoCallbackUrl || '#',
        icon: 'icon-trainings',
        dataPurpose: 'go_to_my_trainings_link',
      },
    ];
  }

  const items = [];

  if (permissions.widgetsEnabled) {
    items.push({
      key: 'dashboard',
      label: 'js.navigation.dashboard',
      href: routes.learnerDashboard,
      icon: 'icon-dashboard',
      dataPurpose: 'go_to_dashboard_link',
    });
  }

  if (permissions.anyLearningPath) {
    items.push({
      key: 'learning_paths',
      label: 'js.navigation.my_learning_paths',
      href: routes.learnerMyLearningPaths,
      icon: 'icon-learning_paths',
      dataPurpose: 'go_to_my_learning_paths_link',
    });
  }

  items.push({
    key: 'my_trainings',
    label: 'js.navigation.my_trainings',
    href: routes.learnerMyTrainings,
    icon: 'icon-trainings',
    dataPurpose: 'go_to_my_trainings_link',
  });

  if (permissions.anyCatalog) {
    items.push({
      key: 'catalogs',
      label: 'js.navigation.catalogs',
      href: routes.learnerCatalogs,
      icon: 'icon-catalogs',
      dataPurpose: 'go_to_catalog_link',
    });
  }

  if (permissions.placementTestEnabled && permissions.showPlacementTestForLearner) {
    items.push({
      key: 'placement_tests',
      label: 'js.navigation.my_placement_tests',
      href: routes.learnerMyPlacementTests,
      icon: 'icon-placement_test',
      dataPurpose: 'go_to_my_placement_tests_link',
    });
  }

  items.push({
    key: 'certificates',
    label: 'js.navigation.my_certificates',
    href: routes.learnerMyCertificates,
    icon: 'icon-certificates',
    dataPurpose: 'go_to_my_certificates_link',
  });

  items.push({
    key: 'my_profile',
    label: 'js.navigation.my_profile',
    href: routes.learnerMyReporting,
    icon: 'icon-reporting',
    dataPurpose: 'go_to_my_profile_link',
  });

  if (permissions.messengerEnabled) {
    items.push({
      key: 'messenger',
      label: 'js.navigation.messaging',
      href: routes.learnerMessengers,
      icon: 'icon-messaging',
      dataPurpose: 'go_to_messenger_link',
    });
  }

  return items;
};

// ─── Trainer ────────────────────────────────────────────────────────────────

export const getTrainerNavConfig = (routes, permissions) => {
  const items = [];

  items.push({
    key: 'learning_paths',
    label: 'js.navigation.learning_path',
    href: routes.trainerMyLearningPaths,
    icon: 'icon-learning_paths',
    dataPurpose: 'go_to_learning_path_link',
  });

  items.push({
    key: 'my_trainings',
    label: 'js.navigation.my_trainings',
    href: routes.trainerMyTrainings,
    icon: 'icon-trainings',
    dataPurpose: 'go_to_my_trainings_link',
  });

  items.push({
    key: 'my_modules',
    label: 'js.navigation.my_modules',
    href: routes.trainerMyModules,
    icon: 'icon-modules',
    dataPurpose: 'go_to_my_modules_link',
  });

  if (permissions.placementTestEnabled) {
    items.push({
      key: 'placement',
      label: 'js.navigation.placement',
      href: '#',
      icon: 'icon-placement_test',
      dataPurpose: 'list_placement_tests_container_link',
      isDropdown: true,
      children: [
        {
          key: 'tests',
          label: 'js.navigation.tests',
          href: routes.trainerMyPlacementTests,
          icon: 'icon-dot',
          dataPurpose: 'go_to_my_placement_tests_link',
        },
        {
          key: 'themes',
          label: 'js.navigation.themes',
          href: routes.trainerThemes,
          icon: 'icon-dot',
          dataPurpose: 'go_to_my_themes_link',
        },
      ],
    });
  }

  if (permissions.canShowReporting) {
    const trainerReportsChildren = [
      {
        key: 'reports_dropdown',
        label: 'js.layouts.admin.top_nav_bar.main_navigation.reports',
        href: '#',
        isDropdown: true,
        activeStateKey: 'trainerIsReporting',
        dataPurpose: 'reports_dropdown_link',
        children: [
          {
            key: 'report_courses',
            label: 'js.layouts.admin.top_nav_bar.main_navigation.report_courses',
            href: routes.trainerTrainingReportings || '#',
            activeStateKey: 'trainerReportCourses',
            dataPurpose: 'go_to_reporting_link',
          },
          {
            key: 'report_learners',
            label: 'js.layouts.admin.top_nav_bar.main_navigation.report_learners',
            href: routes.trainerLearnerReportings || '#',
            activeStateKey: 'trainerReportLearners',
          },
          {
            key: 'report_surveys',
            label: 'js.layouts.admin.top_nav_bar.main_navigation.report_surveys',
            href: routes.trainerSurveyStats || '#',
            activeStateKey: 'trainerReportSurveyStats',
          },
          {
            key: 'report_assessments',
            label: 'js.layouts.admin.top_nav_bar.main_navigation.report_assessments',
            href: routes.trainerAssessmentStats || '#',
            activeStateKey: 'trainerReportAssessmentStats',
          },
          {
            key: 'report_modules',
            label: 'js.layouts.admin.top_nav_bar.main_navigation.modules',
            href: routes.trainerProtomoduleReportings || '#',
            activeStateKey: 'trainerReportModules',
          },
          {
            key: 'report_regulatory',
            label: 'js.layouts.admin.top_nav_bar.main_navigation.report_regulatory',
            href: routes.trainerOpcaReportings || '#',
            activeStateKey: 'trainerReportOpca',
          },
        ],
      },
    ];

    if (permissions.canShowAttestationReportings) {
      trainerReportsChildren.push({
        key: 'attestation_reportings',
        label: 'js.layouts.admin.top_nav_bar.main_navigation.report_attestations',
        href: routes.trainerAttestationReportings,
        dataPurpose: 'display_attestion_reportings_link',
      });
    }

    trainerReportsChildren.push({
      key: 'custom_report',
      label: 'js.navigation.custom_reportings',
      href: routes.trainerCustomReportings || '#',
      dataPurpose: 'custom_reporting_link',
      activeStateKey: 'trainerCustomReporting',
    });

    items.push({
      key: 'reports_section',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.reports_tracking',
      isSection: true,
      icon: 'icon-reporting',
      href: '#',
      dataPurpose: 'reporting_container_link',
      children: trainerReportsChildren,
    });
  }

  items.push({
    key: 'to_do',
    label: 'js.navigation.to_do',
    href: '#',
    icon: 'icon-to_do',
    dataPurpose: 'list_to_do_container',
    isDropdown: true,
    children: [
      {
        key: 'classes',
        label: 'js.navigation.classes',
        href: routes.trainerTodoClasses,
        icon: 'icon-dot',
        dataPurpose: 'go_to_trainer_todo_classes_link',
      },
      {
        key: 'projects',
        label: 'js.navigation.projects',
        href: routes.trainerTodoProjects,
        icon: 'icon-dot',
        dataPurpose: 'go_to_trainer_todo_projects_link',
      },
    ],
  });

  if (permissions.messengerEnabled) {
    items.push({
      key: 'messenger',
      label: 'js.navigation.messaging',
      href: routes.trainerMessengers,
      icon: 'icon-messaging',
      dataPurpose: 'go_to_messenger_link',
    });
  }

  return items;
};

// ─── Manager ────────────────────────────────────────────────────────────────

export const getManagerNavConfig = (routes, permissions) => {
  const items = [];

  const managerReportsChildren = [
    {
      key: 'reports_dropdown',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.reports',
      href: '#',
      isDropdown: true,
      activeStateKey: 'managerIsReporting',
      dataPurpose: 'reports_dropdown_link',
      children: [
        {
          key: 'report_courses',
          label: 'js.layouts.admin.top_nav_bar.main_navigation.report_courses',
          href: routes.managerTrainingReportings || '#',
          activeStateKey: 'managerReportCourses',
          dataPurpose: 'go_to_reporting_link',
        },
        {
          key: 'report_learners',
          label: 'js.layouts.admin.top_nav_bar.main_navigation.report_learners',
          href: routes.managerLearnerReportings || '#',
          activeStateKey: 'managerReportLearners',
        },
        {
          key: 'report_assessments',
          label: 'js.layouts.admin.top_nav_bar.main_navigation.report_assessments',
          href: routes.managerAssessmentStats || '#',
          activeStateKey: 'managerReportAssessmentStats',
        },
        {
          key: 'report_surveys',
          label: 'js.layouts.admin.top_nav_bar.main_navigation.report_surveys',
          href: routes.managerSurveyStats || '#',
          activeStateKey: 'managerReportSurveyStats',
        },
        {
          key: 'report_modules',
          label: 'js.layouts.admin.top_nav_bar.main_navigation.modules',
          href: routes.managerProtomoduleReportings || '#',
          activeStateKey: 'managerReportModules',
        },
        {
          key: 'report_regulatory',
          label: 'js.layouts.admin.top_nav_bar.main_navigation.report_regulatory',
          href: routes.managerOpcaReportings || '#',
          activeStateKey: 'managerReportOpca',
        },
      ],
    },
  ];

  if (permissions.canShowAttestationReportings) {
    managerReportsChildren.push({
      key: 'attestation_reportings',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.report_attestations',
      href: routes.managerAttestationReportings,
      dataPurpose: 'display_attestion_reportings_link',
    });
  }

  managerReportsChildren.push(
    {
      key: 'custom_reportings',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.custom_reportings',
      href: routes.managerCustomReportings || '#',
      dataPurpose: 'custom_reporting_link',
    },
    {
      key: 'learning_path_dashboard',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.lp_certificates_dashboard',
      href: routes.managerLearningPathDashboard || '#',
      dataPurpose: 'list_learning_path_dashboard_link',
    },
    {
      key: 'v2_certificates_dashboard',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.certificates_dashboard',
      href: routes.managerV2CertificatesDashboard || '#',
      dataPurpose: 'list_v2_certificates_dashboard_link',
    },
  );

  items.push({
    key: 'reports_section',
    label: 'js.layouts.admin.top_nav_bar.main_navigation.reports_tracking',
    isSection: true,
    icon: 'icon-reporting',
    href: '#',
    dataPurpose: 'reporting_container_link',
    children: managerReportsChildren,
  });

  items.push({
    key: 'enrollments',
    label: 'js.layouts.admin.top_nav_bar.main_navigation.enrollments',
    href: routes.managerTrainingEnrollments,
    icon: 'icon-modules',
  });

  if (permissions.managerManageUsersEnabled) {
    items.push({
      key: 'users',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.users',
      href: routes.managerUsers,
      icon: 'icon-users',
      dataPurpose: 'list_manager_users_link',
    });
  }

  return items;
};

// ─── Group Leader ────────────────────────────────────────────────────────────

export const getGroupLeaderNavConfig = (routes) => {
  return [
    {
      key: 'groups',
      label: 'js.navigation.my_groups',
      href: routes.groups,
      icon: 'icon-trainings',
      dataPurpose: 'go_to_groups_link',
    },
    {
      key: 'catalogs',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.catalogs',
      href: '#',
      icon: 'icon-catalogs',
      dataPurpose: 'list_catalogs_container_link',
      isDropdown: true,
      children: [
        {
          key: 'enrollments',
          label: 'js.layouts.admin.top_nav_bar.main_navigation.enrollments',
          href: routes.groupLeaderTrainingEnrollments,
          icon: 'icon-dot',
          dataPurpose: 'list_enrollment_link',
        },
      ],
    },
    {
      key: 'to_do',
      label: 'js.navigation.to_do',
      href: '#',
      icon: 'icon-to_do',
      dataPurpose: 'list_to_do_container',
      isDropdown: true,
      children: [
        {
          key: 'classes',
          label: 'js.navigation.classes',
          href: routes.todoClasses,
          icon: 'icon-dot',
          dataPurpose: 'go_to_groups_todo_classes_link',
        },
        {
          key: 'projects',
          label: 'js.navigation.projects',
          href: routes.todoProjects,
          icon: 'icon-dot',
          dataPurpose: 'go_to_groups_todo_projects_link',
        },
      ],
    },
  ];
};

// ─── Admin — Default portal ──────────────────────────────────────────────────

export const getAdminDefaultNavConfig = (routes, permissions) => {
  // ── Users ─────────────────────────────────────────────────────────────────
  const usersSection = {
    key: 'users_section',
    label: 'js.layouts.admin.top_nav_bar.main_navigation.users',
    isSection: true,
    icon: 'icon-users',
    href: '#',
    dataPurpose: 'list_users_container_link',
    children: [
      {
        key: 'users_list',
        label: 'js.layouts.admin.top_nav_bar.main_navigation.users',
        href: routes.adminUsers || '#',
        dataPurpose: 'list_users_link',
      },
      {
        key: 'groups',
        label: 'js.layouts.admin.top_nav_bar.main_navigation.groups',
        href: routes.adminGroups || '#',
        dataPurpose: 'list_groups_link',
      },
      {
        key: 'filters',
        label: 'js.layouts.admin.top_nav_bar.main_navigation.filters',
        href: routes.adminFilters || '#',
        dataPurpose: 'list_filters_link',
      },
      {
        key: 'custom_fields',
        label: 'js.layouts.admin.top_nav_bar.main_navigation.custom_attributes',
        href: routes.adminCustomAttributes || '#',
        dataPurpose: 'list_custom_attributes_link',
      },
    ],
  };

  // ── Learning Content ──────────────────────────────────────────────────────
  const learningChildren = [
    {
      key: 'learning_paths',
      label: 'js.navigation.learning_path',
      href: routes.adminLearningPaths || '#',
      dataPurpose: 'list_learning_path_link',
    },
    {
      key: 'trainings',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.trainings',
      href: routes.adminTrainings || '#',
      dataPurpose: 'list_trainings_link',
    },
    {
      key: 'modules',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.modules',
      href: routes.adminModules || '#',
      dataPurpose: 'list_modules_link',
    },
  ];
  if (permissions.placementTestEnabled) {
    learningChildren.push({
      key: 'assessment_tests',
      label: 'js.navigation.tests',
      href: routes.adminPlacementTests || '#',
      dataPurpose: 'go_to_my_placement_tests_link',
    });
  }
  learningChildren.push(
    {
      key: 'categories',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.categories',
      href: routes.adminCategories || '#',
      dataPurpose: 'list_categories_link',
    },
    {
      key: 'cover_images',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.training_covers',
      href: routes.adminTrainingCovers || '#',
      dataPurpose: 'list_training_covers_link',
    },
    {
      key: 'cert_templates',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.certificate_templates',
      href: routes.adminCertificateTemplates || '#',
      dataPurpose: 'list_certificate_templates_link',
    },
  );
  if (permissions.attestationEnabled) {
    learningChildren.push({
      key: 'attestations',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.attestations',
      href: routes.adminAttestations || '#',
      dataPurpose: 'list_attestations_link',
    });
  }
  learningChildren.push({
    key: 'training_cf',
    label: 'js.layouts.admin.top_nav_bar.main_navigation.custom_attributes',
    href: routes.adminTrainingCustomFields || '#',
    dataPurpose: 'list_training_custom_fields_link',
  });
  if (permissions.canManageSkills) {
    learningChildren.push({
      key: 'skills',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.skills',
      href: routes.adminSkills || '#',
      dataPurpose: 'list_skills_link',
    });
  }
  learningChildren.push(
    {
      key: 'external_tools',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.external_tool',
      href: routes.adminExternalTools || '#',
      dataPurpose: 'external_tool_link',
    },
    {
      key: 'lti_tools',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.lti_tool',
      href: routes.adminLtiTools || '#',
      dataPurpose: 'lti_tool_link',
    },
  );
  if (permissions.placementTestEnabled) {
    learningChildren.push({
      key: 'themes',
      label: 'js.navigation.themes',
      href: routes.adminThemes || '#',
      dataPurpose: 'go_to_my_themes_link',
    });
  }
  const learningSection = {
    key: 'learning_content_section',
    label: 'js.layouts.admin.top_nav_bar.main_navigation.learning_content',
    isSection: true,
    icon: 'icon-trainings',
    href: '#',
    dataPurpose: 'list_trainings_container_link',
    children: learningChildren,
  };

  // ── Catalog & Distribution ────────────────────────────────────────────────
  const catalogChildren = [
    {
      key: 'catalogs_list',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.catalogs',
      href: routes.adminCatalogs || '#',
      dataPurpose: 'list_catalogs_link',
    },
    {
      key: 'pending_validation',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.enrollments',
      href: routes.adminTrainingEnrollments || '#',
      dataPurpose: 'list_enrollment_link',
    },
  ];
  if (permissions.canManagePortals) {
    catalogChildren.push({
      key: 'portals',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.partner_portals',
      href: routes.adminPortals || '#',
      dataPurpose: 'list_portals_link',
    });
  }
  const catalogSection = {
    key: 'catalog_section',
    label: 'js.layouts.admin.top_nav_bar.main_navigation.catalog_distribution',
    isSection: true,
    icon: 'icon-catalogs',
    href: '#',
    dataPurpose: 'list_catalogs_container_link',
    children: catalogChildren,
  };

  // ── Reports & Tracking ────────────────────────────────────────────────────
  const reportsChildren = [
    {
      key: 'reports_dropdown',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.reports',
      href: '#',
      isDropdown: true,
      activeStateKey: 'adminIsReporting',
      dataPurpose: 'reports_dropdown_link',
      children: [
        {
          key: 'report_courses',
          label: 'js.layouts.admin.top_nav_bar.main_navigation.report_courses',
          href: routes.adminTrainingReportings || '#',
          activeStateKey: 'adminReportCourses',
          dataPurpose: 'go_to_reporting_link',
        },
        {
          key: 'report_learners',
          label: 'js.layouts.admin.top_nav_bar.main_navigation.report_learners',
          href: routes.adminLearnerReportings || '#',
          activeStateKey: 'adminReportLearners',
        },
        {
          key: 'report_assessments',
          label: 'js.layouts.admin.top_nav_bar.main_navigation.report_assessments',
          href: routes.adminAssessmentStats || '#',
          activeStateKey: 'adminReportAssessmentStats',
        },
        {
          key: 'report_surveys',
          label: 'js.layouts.admin.top_nav_bar.main_navigation.report_surveys',
          href: routes.adminSurveyStats || '#',
          activeStateKey: 'adminReportSurveyStats',
        },
        {
          key: 'report_modules',
          label: 'js.layouts.admin.top_nav_bar.main_navigation.modules',
          href: routes.adminProtomoduleReportings || '#',
          activeStateKey: 'adminReportModules',
        },
        {
          key: 'report_regulatory',
          label: 'js.layouts.admin.top_nav_bar.main_navigation.report_regulatory',
          href: routes.adminOpcaReportings || '#',
          activeStateKey: 'adminReportOpca',
        },
      ],
    },
    {
      key: 'report_attestations',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.report_attestations',
      href: routes.adminAttestationReportings || '#',
      activeStateKey: 'adminReportAttestations',
      dataPurpose: 'display_attestion_reportings_link',
    },
    {
      key: 'custom_report',
      label: 'js.navigation.custom_reportings',
      href: routes.adminCustomReportings || '#',
      dataPurpose: 'custom_reporting_link',
      activeStateKey: 'adminCustomReporting',
    },
    {
      key: 'learning_path_dashboard',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.lp_certificates_dashboard',
      href: routes.adminLearningPathDashboard || '#',
      dataPurpose: 'list_learning_path_dashboard_link',
    },
    {
      key: 'v2_certificates_dashboard',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.certificates_dashboard',
      href: routes.adminV2CertificatesDashboard || '#',
      dataPurpose: 'list_v2_certificates_dashboard_link',
    },
    {
      key: 'account_status',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.account_status',
      href: routes.adminAccountStatus || '#',
      dataPurpose: 'account_status_link',
    },
  ];
  if (permissions.recurlyToken) {
    reportsChildren.push({
      key: 'billing',
      label: 'js.navigation.billing',
      href: `https://dokeos.recurly.com/account/${permissions.recurlyToken}`,
      dataPurpose: 'billing_link',
      external: true,
    });
  }
  const reportsSection = {
    key: 'reports_section',
    label: 'js.layouts.admin.top_nav_bar.main_navigation.reports_tracking',
    isSection: true,
    icon: 'icon-reporting',
    href: '#',
    dataPurpose: 'reporting_container_link',
    children: reportsChildren,
  };

  // ── Settings & Configuration ──────────────────────────────────────────────
  const settingsChildren = [
    {
      key: 'customize',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.customize',
      href: routes.adminCustomization || '#',
      dataPurpose: 'customize_plateform_link',
    },
    {
      key: 'learner_dash',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.learner_dashboard_settings',
      href: routes.adminLearnerDashboardSetting || '#',
      dataPurpose: 'learner_dashboard_settings_link',
    },
    {
      key: 'email_templates',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.email_templates',
      href: routes.adminEmailTemplates || '#',
      dataPurpose: 'list_email_templates_link',
    },
    {
      key: 'email_dashboard',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.email_dashboard',
      href: routes.adminEmailDashboard || '#',
      dataPurpose: 'list_email_dashboard_link',
    },
    {
      key: 'api',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.api',
      href: routes.adminApiIntegration || '#',
      dataPurpose: 'api_link',
    },
  ];
  if (permissions.webhookEnabled) {
    settingsChildren.push({
      key: 'webhooks',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.webhooks',
      href: routes.adminWebhooks || '#',
      dataPurpose: 'display_webhook_link',
    });
  }
  const settingsSection = {
    key: 'settings_section',
    label: 'js.layouts.admin.top_nav_bar.main_navigation.settings_configuration',
    isSection: true,
    icon: 'icon-settings',
    href: '#',
    dataPurpose: 'list_parameters_container_link',
    children: settingsChildren,
  };

  // ── To Do ─────────────────────────────────────────────────────────────────
  const todoSection = {
    key: 'todo_section',
    label: 'js.layouts.admin.top_nav_bar.main_navigation.to_do',
    isSection: true,
    icon: 'icon-to_do',
    href: '#',
    dataPurpose: 'list_to_do_container',
    children: [
      {
        key: 'classes',
        label: 'js.navigation.classes',
        href: routes.adminTodoClasses || '#',
        dataPurpose: 'go_to_admin_todo_classes_link',
      },
      {
        key: 'projects',
        label: 'js.navigation.projects',
        href: routes.adminTodoProjects || '#',
        dataPurpose: 'go_to_admin_todo_projects_link',
      },
    ],
  };

  return [
    usersSection,
    learningSection,
    catalogSection,
    reportsSection,
    settingsSection,
    todoSection,
  ];
};

// ─── Admin — Partner portal ──────────────────────────────────────────────────

export const getAdminPartnerNavConfig = (routes, permissions) => {
  // ── Users ─────────────────────────────────────────────────────────────────
  const usersSection = {
    key: 'users_section',
    label: 'js.layouts.admin.top_nav_bar.main_navigation.users',
    isSection: true,
    icon: 'icon-users',
    href: '#',
    dataPurpose: 'list_users_container_link',
    children: [
      {
        key: 'users_list',
        label: 'js.layouts.admin.top_nav_bar.main_navigation.users',
        href: routes.adminUsers || '#',
        dataPurpose: 'list_users_link',
      },
      {
        key: 'groups',
        label: 'js.layouts.admin.top_nav_bar.main_navigation.groups',
        href: routes.adminGroups || '#',
        dataPurpose: 'list_groups_link',
      },
      {
        key: 'filters',
        label: 'js.layouts.admin.top_nav_bar.main_navigation.filters',
        href: routes.adminFilters || '#',
        dataPurpose: 'list_filters_link',
      },
      {
        key: 'custom_fields',
        label: 'js.layouts.admin.top_nav_bar.main_navigation.custom_attributes',
        href: routes.adminCustomAttributes || '#',
        dataPurpose: 'list_custom_attributes_link',
      },
    ],
  };

  // ── Learning Content ──────────────────────────────────────────────────────
  const learningChildren = [
    {
      key: 'learning_paths',
      label: 'js.navigation.learning_path',
      href: routes.adminLearningPaths || '#',
      dataPurpose: 'list_learning_path_link',
    },
    {
      key: 'trainings',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.trainings',
      href: routes.adminTrainings || '#',
      dataPurpose: 'list_trainings_link',
    },
  ];
  if (permissions.placementTestEnabled) {
    learningChildren.push({
      key: 'assessment_tests',
      label: 'js.navigation.tests',
      href: routes.adminPlacementTests || '#',
      dataPurpose: 'go_to_placement_tests_link',
    });
  }
  const learningSection = {
    key: 'learning_content_section',
    label: 'js.layouts.admin.top_nav_bar.main_navigation.learning_content',
    isSection: true,
    icon: 'icon-trainings',
    href: '#',
    dataPurpose: 'list_trainings_container_link',
    children: learningChildren,
  };

  // ── Catalog & Distribution ────────────────────────────────────────────────
  const catalogSection = {
    key: 'catalog_section',
    label: 'js.layouts.admin.top_nav_bar.main_navigation.catalog_distribution',
    isSection: true,
    icon: 'icon-catalogs',
    href: '#',
    dataPurpose: 'list_catalogs_container_link',
    children: [
      {
        key: 'catalogs_list',
        label: 'js.layouts.admin.top_nav_bar.main_navigation.catalogs',
        href: routes.adminCatalogs || '#',
        dataPurpose: 'list_catalogs_link',
      },
      {
        key: 'pending_validation',
        label: 'js.layouts.admin.top_nav_bar.main_navigation.enrollments',
        href: routes.adminTrainingEnrollments || '#',
        dataPurpose: 'list_enrollment_link',
      },
    ],
  };

  // ── Reports & Tracking ────────────────────────────────────────────────────
  const reportsChildren = [
    {
      key: 'reports_dropdown',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.reports',
      href: '#',
      isDropdown: true,
      activeStateKey: 'adminIsReporting',
      dataPurpose: 'reports_dropdown_link',
      children: [
        {
          key: 'report_courses',
          label: 'js.layouts.admin.top_nav_bar.main_navigation.report_courses',
          href: routes.adminTrainingReportings || '#',
          activeStateKey: 'adminReportCourses',
          dataPurpose: 'go_to_reporting_link',
        },
        {
          key: 'report_learners',
          label: 'js.layouts.admin.top_nav_bar.main_navigation.report_learners',
          href: routes.adminLearnerReportings || '#',
          activeStateKey: 'adminReportLearners',
        },
        {
          key: 'report_assessments',
          label: 'js.layouts.admin.top_nav_bar.main_navigation.report_assessments',
          href: routes.adminAssessmentStats || '#',
          activeStateKey: 'adminReportAssessmentStats',
        },
        {
          key: 'report_surveys',
          label: 'js.layouts.admin.top_nav_bar.main_navigation.report_surveys',
          href: routes.adminSurveyStats || '#',
          activeStateKey: 'adminReportSurveyStats',
        },
        {
          key: 'report_modules',
          label: 'js.layouts.admin.top_nav_bar.main_navigation.modules',
          href: routes.adminProtomoduleReportings || '#',
          activeStateKey: 'adminReportModules',
        },
        {
          key: 'report_regulatory',
          label: 'js.layouts.admin.top_nav_bar.main_navigation.report_regulatory',
          href: routes.adminOpcaReportings || '#',
          activeStateKey: 'adminReportOpca',
        },
      ],
    },
    {
      key: 'report_attestations',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.report_attestations',
      href: routes.adminAttestationReportings || '#',
      activeStateKey: 'adminReportAttestations',
      dataPurpose: 'display_attestion_reportings_link',
    },
    {
      key: 'account_status',
      label: 'js.layouts.admin.top_nav_bar.main_navigation.account_status',
      href: routes.adminAccountStatus || '#',
      dataPurpose: 'account_status_link',
    },
  ];
  if (permissions.recurlyToken) {
    reportsChildren.push({
      key: 'billing',
      label: 'js.navigation.billing',
      href: `https://dokeos.recurly.com/account/${permissions.recurlyToken}`,
      dataPurpose: 'billing_link',
      external: true,
    });
  }
  const reportsSection = {
    key: 'reports_section',
    label: 'js.layouts.admin.top_nav_bar.main_navigation.reports_tracking',
    isSection: true,
    icon: 'icon-reporting',
    href: '#',
    dataPurpose: 'reporting_container_link',
    children: reportsChildren,
  };

  // ── Settings & Configuration ──────────────────────────────────────────────
  const settingsSection = {
    key: 'settings_section',
    label: 'js.layouts.admin.top_nav_bar.main_navigation.settings_configuration',
    isSection: true,
    icon: 'icon-settings',
    href: '#',
    dataPurpose: 'list_parameters_container_link',
    children: [
      {
        key: 'customize',
        label: 'js.layouts.admin.top_nav_bar.main_navigation.customize',
        href: routes.adminCustomization || '#',
        dataPurpose: 'customize_plateform_link',
      },
      {
        key: 'learner_dash',
        label: 'js.layouts.admin.top_nav_bar.main_navigation.learner_dashboard_settings',
        href: routes.adminLearnerDashboardSetting || '#',
        dataPurpose: 'learner_dashboard_settings_link',
      },
      {
        key: 'email_templates',
        label: 'js.layouts.admin.top_nav_bar.main_navigation.email_templates',
        href: routes.adminEmailTemplates || '#',
        dataPurpose: 'display_email_templates_link',
      },
      {
        key: 'email_dashboard',
        label: 'js.layouts.admin.top_nav_bar.main_navigation.email_dashboard',
        href: routes.adminEmailDashboard || '#',
        dataPurpose: 'list_email_dashboard_link',
      },
    ],
  };

  // ── To Do ─────────────────────────────────────────────────────────────────
  const todoSection = {
    key: 'todo_section',
    label: 'js.layouts.admin.top_nav_bar.main_navigation.to_do',
    isSection: true,
    icon: 'icon-to_do',
    href: '#',
    dataPurpose: 'list_to_do_container',
    children: [
      {
        key: 'classes',
        label: 'js.navigation.classes',
        href: routes.adminTodoClasses || '#',
        dataPurpose: 'go_to_admin_todo_classes_link',
      },
      {
        key: 'projects',
        label: 'js.navigation.projects',
        href: routes.adminTodoProjects || '#',
        dataPurpose: 'go_to_admin_todo_projects_link',
      },
    ],
  };

  return [
    usersSection,
    learningSection,
    catalogSection,
    reportsSection,
    settingsSection,
    todoSection,
  ];
};
